"use client"

import gsap, { ScrollTrigger, SplitText } from "gsap/all"
import { css, f, styled } from "library/styled"
import { useAnimation } from "library/useAnimation"
import { getMedia } from "library/useMedia"
import { type ReactNode, useRef } from "react"
import colors from "styles/colors"

gsap.registerPlugin(SplitText, ScrollTrigger)

interface SplitTextAnimationProps {
	children: ReactNode
	className?: string
	fromColor?: string
	toColor?: string
	start?: string
	end?: string
	scrub?: boolean | number
}

export default function SplitTextAnimation({
	children,
	className,
	fromColor = colors.js.green100,
	toColor = colors.js.green300,
	start = "top 90%",
	end = getMedia("bottom 40%", "bottom 40%", "bottom 40%", "bottom 50%"),
	scrub = true,
}: SplitTextAnimationProps) {
	const elementRef = useRef<HTMLDivElement>(null)

	useAnimation(
		() => {
			if (!elementRef.current) return

			const split = new SplitText(elementRef.current, {
				type: "chars, words",
				reduceWhiteSpace: false,
				linesClass: "split-line",
			})

			gsap.set(split.chars, { color: fromColor })

			ScrollTrigger.create({
				trigger: elementRef.current,
				start,
				end,
				scrub,
				onUpdate: (self) => {
					const progress = self.progress

					const totalChars = split.chars.length
					const activeChars = Math.round(progress * totalChars)

					for (let i = 0; i < totalChars; i++) {
						const char = split.chars[i]
						if (!char) continue
						gsap.set(char, {
							color: i < activeChars ? toColor : fromColor,
						})
					}
				},
			})
		},
		[start, end, toColor, fromColor, scrub],
		{ recreateOnResize: true },
	)

	return (
		<Wrapper ref={elementRef} className={className}>
			{children}
		</Wrapper>
	)
}

const Wrapper = styled("div", {
	...f.responsive(css`
		overflow-wrap: break-word;
	`),
})
